import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, RefreshCw, LogOut, Lock } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

const TABS = [
  { id: "experiences", label: "Experiences", template: { name: "New Experience", slug: "", category: "Adventure", theme: "Free-Roaming Immersive VR", tagline: "", description: "", duration: "30 min", age: "10+", participants: "1–6", space: "2,000 sq ft", status: "Coming Soon", hue: 222, featured: false, placeholder: true } },
  { id: "games", label: "VR Games", template: { name: "New Attraction", slug: "", category: "", theme: "", tagline: "", description: "", duration: "5–10 min", age: "10+", space: "150 sq ft", equipment: "", capacity: "8 players/hour", suitability: "", installation: "", hue: 14, featured: false } },
  { id: "locations", label: "Locations", template: { city: "New City", slug: "", venue: "", status: "Upcoming", experiences: "", detail: "", hue: 222 } },
  { id: "news", label: "News & Blog", template: { title: "New Article", slug: "", category: "Company News", excerpt: "", body: "", date: new Date().toISOString().slice(0, 10), hue: 222 } },
  { id: "faqs", label: "FAQs", template: { question: "", answer: "" } },
  { id: "testimonials", label: "Testimonials", template: { quote: "", name: "", role: "" } },
  { id: "jobs", label: "Jobs", template: { title: "", location: "Hyderabad", type: "Full-time", detail: "" } },
  { id: "team", label: "Team", template: { name: "", role: "", detail: "" } },
  { id: "partners", label: "Partners", template: { name: "", slug: "", url: "https://", logo: "", order: 99, type: "Partner", tagline: "", detail: "", hue: 222 } },
  { id: "enquiries", label: "Enquiries", readonly: true },
  { id: "partnerships", label: "Partnerships", readonly: true },
  { id: "newsletters", label: "Newsletter", readonly: true },
];

const docLabel = (d) => d.name || d.title || d.city || d.question || d.company || d.email || d.id;

const LoginScreen = ({ onAuthed }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.login(email.trim().toLowerCase(), password);
      onAuthed();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#04060B] flex items-center justify-center px-6" data-testid="admin-login-screen">
      <form onSubmit={login} className="w-full max-w-sm rounded-2xl border border-cyan-400/20 bg-cyan-400/[0.03] backdrop-blur-xl p-10 shadow-[0_0_100px_rgba(34,211,238,0.08)]">
        <div className="flex items-center gap-3 mb-2">
          <Lock size={18} className="text-cyan-300" />
          <h1 className="font-serif text-3xl tracking-tight">Vrova CMS<span className="text-cyan-300">.</span></h1>
        </div>
        <p className="text-xs font-mono text-zinc-600 mb-8">Restricted area — sign in to manage content.</p>
        <div className="space-y-5">
          <div>
            <label htmlFor="admin-email">Email</label>
            <input id="admin-email" data-testid="admin-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" />
          </div>
          <div>
            <label htmlFor="admin-password">Password</label>
            <input id="admin-password" data-testid="admin-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
          </div>
          {error && <p className="text-xs text-red-400 font-mono" data-testid="admin-login-error">{error}</p>}
          <button type="submit" data-testid="admin-login-submit" disabled={busy}
            className="w-full px-8 py-3.5 rounded-full bg-cyan-300 text-black text-sm font-medium hover:bg-cyan-200 transition-colors disabled:opacity-50">
            {busy ? "Signing in…" : "Sign In"}
          </button>
          <a href="/" className="block text-center text-xs font-mono text-zinc-600 hover:text-white transition-colors">← Back to site</a>
        </div>
      </form>
    </main>
  );
};

const DocRow = ({ doc, readonly, onEdit, onDelete }) => {
  const meta = useMemo(
    () => [doc.category, doc.status, doc.type, doc.date, doc.created_at?.slice(0, 10)].filter(Boolean).join(" · "),
    [doc]
  );
  return (
    <li className="py-4 flex items-center justify-between gap-4">
      <button onClick={() => !readonly && onEdit(doc)} data-testid={`admin-item-${doc.id.slice(0, 8)}`}
        className="text-left flex-1 min-w-0">
        <p className="text-sm text-zinc-200 truncate">{docLabel(doc)}</p>
        <p className="text-[11px] font-mono text-zinc-600 truncate mt-1">
          {meta}{doc.placeholder ? " · placeholder" : ""}
        </p>
      </button>
      <button onClick={() => onDelete(doc)} data-testid={`admin-delete-${doc.id.slice(0, 8)}`}
        className="p-2 rounded-full text-zinc-600 hover:text-red-400 transition-colors" aria-label="Delete">
        <Trash2 size={14} />
      </button>
    </li>
  );
};

const DocListPanel = ({ docs, loading, readonly, onRefresh, onAdd, onEdit, onDelete }) => (
  <div className="rounded-2xl border border-cyan-400/10 bg-cyan-400/[0.02] p-6">
    <div className="flex items-center justify-between mb-5">
      <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">{docs.length} items</p>
      <div className="flex gap-2">
        <button onClick={onRefresh} data-testid="admin-refresh" className="p-2.5 rounded-full border border-white/10 text-zinc-400 hover:text-white transition-colors" aria-label="Refresh"><RefreshCw size={14} /></button>
        {!readonly && (
          <button onClick={onAdd} data-testid="admin-add-new"
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-cyan-300 text-black text-xs font-medium hover:bg-cyan-200 transition-colors">
            <Plus size={13} /> Add New
          </button>
        )}
      </div>
    </div>
    {loading ? <p className="text-sm text-zinc-500 font-mono">Loading…</p> : (
      <ul className="divide-y divide-white/[0.06] max-h-[60vh] overflow-y-auto" data-testid="admin-doc-list">
        {docs.map((d) => <DocRow key={d.id} doc={d} readonly={readonly} onEdit={onEdit} onDelete={onDelete} />)}
        {docs.length === 0 && <li className="py-8 text-sm text-zinc-600 font-mono">No items yet.</li>}
      </ul>
    )}
  </div>
);

const SubmissionsPanel = ({ docs }) => (
  <>
    <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-5">Latest submissions</p>
    <div className="space-y-4 max-h-[60vh] overflow-y-auto">
      {docs.slice().reverse().slice(0, 20).map((d) => (
        <div key={d.id} className="rounded-xl border border-white/[0.08] p-5">
          <p className="text-sm text-zinc-200 mb-2">{docLabel(d)}{d.email ? ` — ${d.email}` : ""}</p>
          <pre className="text-[11px] font-mono text-zinc-500 whitespace-pre-wrap break-words">{JSON.stringify(d, null, 1).slice(0, 600)}</pre>
        </div>
      ))}
    </div>
  </>
);

const EditorPanel = ({ editing, json, jsonError, onChange, onSave }) => (
  <>
    <div className="flex items-center justify-between mb-5">
      <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">{editing === "new" ? "New item" : "Edit item"}</p>
      <button onClick={onSave} data-testid="admin-save"
        className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-cyan-300 text-black text-xs font-medium hover:bg-cyan-200 transition-colors">
        <Save size={13} /> Save
      </button>
    </div>
    <textarea value={json} onChange={(e) => onChange(e.target.value)} data-testid="admin-json-editor"
      spellCheck={false} rows={20}
      className="w-full font-mono text-xs leading-relaxed !bg-black/40" />
    {jsonError && <p className="text-xs text-red-400 mt-3 font-mono" data-testid="admin-json-error">{jsonError}</p>}
  </>
);

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState("experiences");
  const [docs, setDocs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [json, setJson] = useState("");
  const [jsonError, setJsonError] = useState("");
  const [loading, setLoading] = useState(false);

  const active = TABS.find((t) => t.id === tab);

  useEffect(() => {
    api.me().then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    api.adminList(tab)
      .then((d) => { setDocs(d); setLoading(false); })
      .catch((err) => {
        setLoading(false);
        if (err.response?.status === 401) { setAuthed(false); return; }
        toast.error("Failed to load");
      });
  }, [tab]);

  useEffect(() => {
    if (!authed) return;
    setEditing(null); setJson(""); setJsonError("");
    load();
  }, [authed, load]);

  const logout = async () => {
    try { await api.logout(); } catch { /* cookie cleared server-side; ignore */ }
    setAuthed(false);
  };

  const startEdit = (doc) => { setEditing(doc || "new"); setJson(JSON.stringify(doc || active.template, null, 2)); setJsonError(""); };

  const save = async () => {
    let payload;
    try {
      payload = JSON.parse(json);
      setJsonError("");
    } catch (e) {
      setJsonError(`Invalid JSON: ${e.message}`);
      return;
    }
    try {
      if (editing === "new") await api.adminCreate(tab, payload);
      else await api.adminUpdate(tab, editing.id, payload);
      toast.success("Saved.");
      setEditing(null); setJson("");
      load();
    } catch (err) {
      if (err.response?.status === 401) { setAuthed(false); return; }
      toast.error("Save failed.");
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete "${docLabel(doc)}"?`)) return;
    try {
      await api.adminDelete(tab, doc.id);
      toast.success("Deleted.");
      load();
    } catch (err) {
      if (err.response?.status === 401) { setAuthed(false); return; }
      toast.error("Delete failed.");
    }
  };

  if (authed === null) {
    return (
      <main className="min-h-screen bg-[#04060B] flex items-center justify-center">
        <p className="text-zinc-500 font-mono text-sm">Checking session…</p>
      </main>
    );
  }

  if (authed === false) return <LoginScreen onAuthed={() => setAuthed(true)} />;

  return (
    <main className="min-h-screen bg-[#04060B] pt-10 pb-28" data-testid="admin-panel">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="flex items-baseline justify-between mb-2">
          <h1 className="font-serif text-4xl tracking-tight">Vrova CMS<span className="text-cyan-300">.</span></h1>
          <div className="flex items-center gap-5">
            <a href="/" data-testid="admin-back-to-site" className="text-xs font-mono text-zinc-500 hover:text-white transition-colors">← View site</a>
            <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-1.5 text-xs font-mono text-zinc-500 hover:text-white transition-colors">
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>
        <p className="text-xs font-mono text-zinc-600 mb-10">Content management · items marked "placeholder" are demo content ready to be replaced with licensed experiences.</p>
        <div className="flex flex-wrap gap-2 mb-10" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`px-5 py-2.5 rounded-full text-xs font-mono uppercase tracking-[0.12em] transition-all duration-300 ${tab === t.id ? "bg-cyan-300 text-black" : "border border-cyan-400/15 text-zinc-400 hover:text-cyan-200 hover:border-cyan-400/40"}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <DocListPanel docs={docs} loading={loading} readonly={active.readonly}
            onRefresh={load} onAdd={() => startEdit(null)} onEdit={startEdit} onDelete={remove} />

          <div className="rounded-2xl border border-cyan-400/10 bg-cyan-400/[0.02] p-6">
            {active.readonly ? (
              <SubmissionsPanel docs={docs} />
            ) : editing ? (
              <EditorPanel editing={editing} json={json} jsonError={jsonError} onChange={setJson} onSave={save} />
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-zinc-600 font-mono py-20">
                Select an item to edit, or add a new one.
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
